import { Sun, Moon, Monitor } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../store/themeStore';

type Mode = 'light' | 'dark' | 'system';

/**
 * Bascule de mode d'affichage — Clair / Sombre / Système.
 *
 * Le mode sombre ne s'applique qu'aux thèmes compatibles (`colorsDark`, ex.
 * Méridien) ; les thèmes clairs conservés restent clairs quel que soit ce choix.
 * En « Système », l'affichage suit la préférence de l'OS.
 */
export default function DisplayModeToggle({ showHint = true }: { showHint?: boolean }) {
  const { t } = useTranslation('settings');
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const supportsDark = useThemeStore((s) => s.theme.supportsDark);

  const options: Array<{ value: Mode; icon: typeof Sun; label: string }> = [
    { value: 'light', icon: Sun, label: t('appearance.mode.light') },
    { value: 'dark', icon: Moon, label: t('appearance.mode.dark') },
    { value: 'system', icon: Monitor, label: t('appearance.mode.system') },
  ];

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-900 mb-2">{t('appearance.mode.title')}</h4>
      <div className="inline-flex bg-gray-100 rounded-lg p-1 gap-1">
        {options.map(({ value, icon: Icon, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            aria-pressed={mode === value}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              mode === value ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>
      {showHint && !supportsDark && (
        <p className="text-xs text-gray-500 mt-2">{t('appearance.mode.hint')}</p>
      )}
    </div>
  );
}
